import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsCard from '../admin-dashboard/components/StatsCard';
import RetentionChart from './components/RetentionChart';
import ChurnRiskList from './components/ChurnRiskList';
import EngagementHeatmap from './components/EngagementHeatmap';
import CohortAnalysis from './components/CohortAnalysis';
import RetentionInsights from './components/RetentionInsights';

const MemberRetentionAnalytics = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    retentionRate: 0,
    churnRate: 0,
    avgEngagementScore: 0,
    atRiskMembers: 0
  });
  const [engagementData, setEngagementData] = useState([]);
  const [churnPredictions, setChurnPredictions] = useState([]);
  const [cohortData, setCohortData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [selectedMetric, setSelectedMetric] = useState('engagement');

  const metrics = [
    { id: 'engagement', label: 'Engagement Score', icon: 'Activity' },
    { id: 'retention', label: 'Retention Rate', icon: 'TrendingUp' },
    { id: 'churn', label: 'Churn Risk', icon: 'AlertTriangle' },
    { id: 'lifecycle', label: 'Member Lifecycle', icon: 'Users' }
  ];

  useEffect(() => {
    if (!authLoading && (!user || !isGymOwner)) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && isGymOwner) {
      loadRetentionData();
    }
  }, [currentTenant, isGymOwner, dateRange]);

  const loadRetentionData = async () => {
    setLoading(true);
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      // Load member stats
      const { data: members, error: membersError } = await supabase
        ?.from('tenant_members')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id);

      if (membersError) throw membersError;

      const totalMembers = members?.length || 0;
      const activeMembers = members?.filter(m => m?.is_active)?.length || 0;
      const retentionRate = totalMembers > 0 ? ((activeMembers / totalMembers) * 100)?.toFixed(1) : 0;
      const churnRate = totalMembers > 0 ? (((totalMembers - activeMembers) / totalMembers) * 100)?.toFixed(1) : 0;

      // Load engagement metrics
      const { data: engagementMetrics, error: engagementError } = await supabase
        ?.from('member_engagement_metrics')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('date', startDate?.toISOString()?.split('T')?.[0])
        ?.order('date', { ascending: true });

      if (engagementError) throw engagementError;

      setEngagementData(engagementMetrics || []);

      // Calculate average engagement score
      const avgEngagement = engagementMetrics?.length > 0
        ? (engagementMetrics?.reduce((sum, m) => sum + (m?.engagement_score || 0), 0) / engagementMetrics?.length)?.toFixed(0)
        : 0;

      // Load churn predictions
      const { data: churnData, error: churnError } = await supabase
        ?.from('churn_predictions')
        ?.select(`
          *,
          user_profiles!churn_predictions_user_id_fkey(id, full_name, email, avatar_url)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('churn_probability', { ascending: false });

      if (churnError) throw churnError;

      setChurnPredictions(churnData || []);

      const atRiskMembers = churnData?.filter(c => 
        c?.churn_risk_level === 'high' || c?.churn_risk_level === 'critical'
      )?.length || 0;

      // Load cohort data
      const { data: cohorts, error: cohortsError } = await supabase
        ?.from('retention_cohorts')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('cohort_month', { ascending: false })
        ?.limit(6);

      if (cohortsError) throw cohortsError;

      setCohortData(cohorts || []);

      setStats({
        totalMembers,
        activeMembers,
        retentionRate,
        churnRate,
        avgEngagementScore: avgEngagement,
        atRiskMembers
      });
    } catch (error) {
      console.error('Error loading retention data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading retention analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      
      <main className="flex-1 px-4 py-6 md:px-8 lg:px-12 max-w-[1440px] mx-auto w-full">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                Member Retention Analytics
              </h1>
              <p className="text-muted-foreground">
                Comprehensive insights into member engagement, churn analysis, and retention strategies
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e?.target?.value)}
                className="px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="180">Last 6 Months</option>
              </select>
              <Button
                variant="outline"
                onClick={loadRetentionData}
                iconName="RefreshCw"
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatsCard
            icon="Users"
            iconColor="var(--color-primary)"
            title="Total Members"
            value={stats?.totalMembers}
            subtitle={`${stats?.activeMembers} active members`}
            trend="up"
            trendValue={`${stats?.retentionRate}%`}
          />
          <StatsCard
            icon="TrendingUp"
            iconColor="var(--color-success)"
            title="Retention Rate"
            value={`${stats?.retentionRate}%`}
            subtitle="Member retention"
            trend={stats?.retentionRate >= 80 ? 'up' : 'down'}
            trendValue={`${stats?.churnRate}% churn`}
          />
          <StatsCard
            icon="Activity"
            iconColor="var(--color-accent)"
            title="Avg Engagement Score"
            value={stats?.avgEngagementScore}
            subtitle="Out of 100"
            trend={stats?.avgEngagementScore >= 60 ? 'up' : 'down'}
            trendValue={stats?.avgEngagementScore >= 60 ? 'Good' : 'Needs attention'}
          />
          <StatsCard
            icon="AlertTriangle"
            iconColor="var(--color-warning)"
            title="At-Risk Members"
            value={stats?.atRiskMembers}
            subtitle="High/critical churn risk"
            trend={stats?.atRiskMembers > 5 ? 'down' : 'up'}
            trendValue={`${((stats?.atRiskMembers / stats?.totalMembers) * 100)?.toFixed(1)}%`}
          />
          <StatsCard
            icon="Target"
            iconColor="var(--color-primary)"
            title="Churn Rate"
            value={`${stats?.churnRate}%`}
            subtitle="Member churn"
            trend={stats?.churnRate < 10 ? 'up' : 'down'}
            trendValue={stats?.churnRate < 10 ? 'Low' : 'High'}
          />
          <StatsCard
            icon="Calendar"
            iconColor="var(--color-accent)"
            title="Cohort Analysis"
            value={cohortData?.length}
            subtitle="Cohorts tracked"
            trend="neutral"
            trendValue="Last 6 months"
          />
        </div>

        {/* Metric Selector */}
        <div className="flex flex-wrap gap-3 mb-6">
          {metrics?.map((metric) => (
            <button
              key={metric?.id}
              onClick={() => setSelectedMetric(metric?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-smooth ${
                selectedMetric === metric?.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-foreground border-border hover:border-primary'
              }`}
            >
              <Icon name={metric?.icon} size={18} />
              <span className="text-sm font-medium">{metric?.label}</span>
            </button>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Left Column - Charts */}
          <div className="lg:col-span-2 space-y-6">
            <RetentionChart data={engagementData} selectedMetric={selectedMetric} />
            <CohortAnalysis data={cohortData} />
          </div>

          {/* Right Column - Insights */}
          <div className="space-y-6">
            <RetentionInsights stats={stats} />
            <EngagementHeatmap data={engagementData} />
          </div>
        </div>

        {/* Churn Risk Analysis */}
        <ChurnRiskList predictions={churnPredictions} onRefresh={loadRetentionData} />
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default MemberRetentionAnalytics;